import DSInput from "@/componentsDS/Input";
import DSInputDropdown from "@/dsComponents/InputDropdownSelect";
import countries from "@/constants/country";
import { useHciImprovement } from "@/hooks/use-hci-improvement";
import { validatePitstopURL } from "@/utils/validate-email";
import { sanitizePitstopUrl } from "@/utils/validate-pitstop";
import { HCI_CODE_REGEX } from "@/utils/validateHCI";
import validateUEN from "@/utils/validateUEN";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Controller } from 'react-hook-form';

const CompanyInfo = ({
	errors,
	register,
	watch,
	setValue,
	infoItems,
	defaultDomain,
	control,
	isFilledWithMyInfo,
	currentProfile,
	trigger
}) => {
	const [countryOfIncorporation, setCountryOfIncorporation] = useState("");
	const [entityTypeVal, setEntityTypeVal] = useState("");

	const [isPitstopUrlModified, setIsPitstopUrlModified] = useState(false);
	const isHciEnabled = useHciImprovement();
	const { company, pitstopUrl, uen, hciCode, countryOfIncorporation: coi, entityType } = watch([
		"company",
		"pitstopUrl",
		"uen",
		"hciCode",
		"countryOfIncorporation",
		"entityType"
	]);

	useEffect(() => {
		if (uen && uen !== uen.toUpperCase()) {
			setValue("uen", uen.toUpperCase(), { shouldValidate: true });
		}
	}, [uen, setValue]);

	useEffect(() => {
		if (hciCode && hciCode !== hciCode.toUpperCase()) {
			setValue("hciCode", hciCode.toUpperCase(), { shouldValidate: true });
		}
	}, [hciCode, setValue]);

	useEffect(() => {
		if (!isPitstopUrlModified && company) {
			const url = sanitizePitstopUrl(company);
			setValue("pitstopUrl", url, { shouldValidate: true });
		}
	}, [company]);

	useEffect(() => {
		if(coi && uen) {
			trigger("uen")
		}
	}, [coi])

	useEffect(() => {
		if (!coi && !isFilledWithMyInfo && infoItems?.countryOfIncorp?.length) {
			const singaporeCountryCode = countries.find((country) => country.cc === "+65")?.value;
			const singapore = infoItems.countryOfIncorp.find(
				(item) => item.id === singaporeCountryCode
			);
			if (singapore) {
				setCountryOfIncorporation(singapore.label);
				setValue("countryOfIncorporation", singapore.id, { shouldValidate: true });
			}
		}
	}, [infoItems?.countryOfIncorp, isFilledWithMyInfo])

	return (
		<>
			<div className="login-box__section-title-buildex">
				Company Information
			</div>
			<div className="login-box__form__cols--buildex">
				<div className="login-box__form__col">
					<DSInput
						name="company"
						label="Company Name as per ACRA"
						placeholder="Enter company name as per ACRA"
						errorMessage={errors.company?.message}
						required={true}
						id="company-input"
						fullWidth={true}
						value={company}
						disabled={isFilledWithMyInfo}
						register={register({
							required: "This field is mandatory",
							minLength: {
								value: 3,
								message: "Please enter a valid company name",
							},
						})}
						setValue={setValue}
					/>
				</div>
				<div className="login-box__form__col">
					<DSInput
						name="pitstopUrl"
						label="Pitstop URL"
						placeholder="Enter pitstop url"
						errorMessage={errors.pitstopUrl?.message}
						required={true}
						id="pitstopUrl-input"
						fullWidth={true}
						setValue={setValue}
						value={pitstopUrl}
						register={register({
							required: "This field is mandatory",
							minLength: {
								value: 3,
								message: "Please enter a valid Pitstop URL that contains only letters, numbers, and dashes.",
							},
							maxLength: {
								value: 37,
								message: "Pitstop URL cannot exceed 37 characters",
							},
							validate: {
								checkEmail: (v) =>
									validatePitstopURL(v) ||
									"Please enter a valid Pitstop URL that contains only letters, numbers, and dashes.",
							},
						})}
						pipeText={pitstopUrl?.trim().toLowerCase() + defaultDomain}
						keyPress={(e) =>
							!isPitstopUrlModified && setIsPitstopUrlModified(true)
						}
					/>
				</div>
				<div className="login-box__form__col login-box__form__col">
					<DSInput
						name="uen"
						label="Company Registration Number / UEN"
						placeholder="Enter Unique Entity Number"
						errorMessage={errors.uen?.message}
						required={true}
						id="uen"
						fullWidth={true}
						setValue={setValue}
						disabled={isFilledWithMyInfo}
						value={uen}
						register={register({
							required: "This field is mandatory",
							validate: {
								checkUEN: (v) =>
									validateUEN(v, coi ?? "SG") ||
									"Please enter a valid UEN",
							},
						})}
					/>
				</div>
				{isHciEnabled && (
					<>
						<div className="login-box__form__col login-box__form__col--half login-box-buildex__dropdown">
							<Controller
								control={control}
								name="entityType"
								rules={{ required: "This field is mandatory" }}
								render={({ onChange: controllerOnChange }) => {
									return (
										<DSInputDropdown
											name="entityType"
											label="Entity Type"
											placeholder="Select Option"
											errorMessage={errors.entityType?.message}
											id="entity-type"
											required={true}
											dropdownOption={[
												{ id: "HCI", label: "MOH-licensed clinic (has HCI Code)" },
												{ id: "NON_HCI", label: "Healthcare entity without HCI Code" }
											]}
											size="default"
											fullWidth={true}
											value={entityTypeVal}
											onChange={setEntityTypeVal}
											onSelectDropdownItem={(item) => {
												controllerOnChange(item[0]?.id);
												if (item[0]?.id === "NON_HCI") {
													setValue("hciCode", "");
												}
											}}
										/>
									);
								}}
							/>
						</div>
						{entityType === "HCI" && (
							<div className="login-box__form__col login-box__form__col--half">
								<DSInput
									name="hciCode"
									label="HCI Code"
									placeholder="Enter HCI code"
									errorMessage={errors.hciCode?.message}
									required={true}
									id="hciCode"
									fullWidth={true}
									setValue={setValue}
									value={hciCode}
									maxLength={7}
									register={register({
										required: "This field is mandatory",
										validate: {
											checkHciCode: (v) =>
												HCI_CODE_REGEX.test(v) ||
												"Please enter a valid HCI code",
										},
									})}
								/>
							</div>
						)}
						{entityType === "NON_HCI" && (
							<div className="login-box__form__col login-box__form__col--half entity-type-note">
								<div className="entity-type-note__text" style={{ fontSize: "12px", color: "#666", marginTop: "24px" }}>
									Entities without an HCI Code are identified by UEN only. Ensure your UEN is correct before proceeding.
								</div>
							</div>
						)}
					</>
				)}
				<div className="login-box__form__col login-box__form__col login-box-buildex__dropdown">
					<Controller
						name="countryOfIncorporation"
						control={control}
						rules={{ required: "This field is required" }}
						render={({ onChange: controllerOnChange }) => {
							return (
								<DSInputDropdown
									name="countryOfIncorporation"
									label="Country of Incorporation"
									placeholder="Select Option"
									errorMessage={
										errors.countryOfIncorporation?.message
									}
									id="company-of-incorporation"
									dropdownOption={
										infoItems?.countryOfIncorp ?? []
									}
									size="default"
									required={true}
									fullWidth={true}
									readOnly={true}
									disabled={isFilledWithMyInfo && currentProfile.organization.countryOfIncorporation}
									value={currentProfile.organization.countryOfIncorporation?.desc ?? countryOfIncorporation}
									onChange={setCountryOfIncorporation}
									onSelectDropdownItem={(item) => {
										controllerOnChange(item[0]?.id);
									}}
								/>
							);
						}}
					/>
				</div>
			</div>

		</>
	);
}


export default CompanyInfo;

CompanyInfo.propTypes = {
	errors: PropTypes.object,
	register: PropTypes.func,
	watch: PropTypes.func,
	setValue: PropTypes.func,
	infoItems: PropTypes.object,
	defaultDomain: PropTypes.string,
	control: PropTypes.object,
	isFilledWithMyInfo: PropTypes.bool,
	currentProfile: PropTypes.object,
	trigger: PropTypes.func
}
